/**
 * SupportChatWidget — floating "Ask Cyannect" bottom-right button + panel.
 *
 * Rendered globally by App.js on every host EXCEPT admin.cyannect.com.
 * Backed by POST /api/public/support/ask (deterministic-only, no AI for
 * anonymous callers). Uses the shared axios instance so CSRF is handled.
 *
 * Behaviour:
 *   - Closed by default, single one-time pulse on first paint to hint at
 *     the widget (localStorage flag suppresses future pulses).
 *   - Opens into a compact chat panel with input, message list, and a
 *     "sign in / register" CTA when the backend says require_signin.
 *   - Conversation id is a per-tab uuid stored in sessionStorage so the
 *     admin analytics can group a session's questions together without
 *     tracking users across visits.
 *   - Every interactive element has a data-testid for automation.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, RotateCcw, ShieldCheck, Mail } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY_PULSED = "cyn:support-chat:pulsed";
const STORAGE_KEY_CONVO = "cyn:support-chat:conversation_id";

function newConversationId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return "c_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getOrCreateConversationId() {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY_CONVO);
    if (existing) return existing;
    const fresh = newConversationId();
    sessionStorage.setItem(STORAGE_KEY_CONVO, fresh);
    return fresh;
  } catch {
    return newConversationId();
  }
}

function resetConversationId() {
  const fresh = newConversationId();
  try { sessionStorage.setItem(STORAGE_KEY_CONVO, fresh); } catch { /* noop */ }
  return fresh;
}

const WELCOME = {
  role: "bot",
  text:
    "Hi 👋 I'm the Cyannect helper. Ask me anything about storefronts, provinces, coupon codes, pricing, or integration.",
};

// ── Contextual chip config ────────────────────────────────────────────
// Each chip's `question` string is fed straight into the same /ask
// pipeline so the response is indistinguishable from a typed question.
// The "Something else" chip is special-cased in-widget — it triggers
// the email-OTP verification flow instead of hitting /ask.
const CHIP_ESCALATE = "__escalate__";

const DEFAULT_CHIPS = [
  // Points at PLAN_COMPARISON so the answer covers both Starter + Growth
  // + Add-ons in one shot.
  { label: "Pricing", question: "How do I compare Starter and Growth?" },
  { label: "Storefronts", question: "How do I create a storefront?" },
  { label: "Integration", question: "How do I integrate Cyannect with my website?" },
  { label: "Something else", question: CHIP_ESCALATE },
];

const CONTEXTUAL_CHIPS = [
  {
    match: /^\/pricing/,
    chips: [
      { label: "Compare plans", question: "How do I compare Starter and Growth?" },
      { label: "Add-ons", question: "How do add-ons work?" },
      { label: "Annual discount", question: "What is the annual add-on discount?" },
      { label: "Something else", question: CHIP_ESCALATE },
    ],
  },
  {
    match: /^(\/api-docs|\/developers|\/docs\/web-sdk)/,
    chips: [
      { label: "SDK install", question: "How do I install the Cyannect Web SDK?" },
      { label: "Webhooks", question: "How do Cyannect webhooks work?" },
      { label: "Conversion event", question: "How do I fire the conversion event?" },
      { label: "Something else", question: CHIP_ESCALATE },
    ],
  },
];

function chipsForPath(pathname) {
  const p = pathname || "/";
  for (const rule of CONTEXTUAL_CHIPS) {
    if (rule.match.test(p)) return rule.chips;
  }
  return DEFAULT_CHIPS;
}

export default function SupportChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([WELCOME]);
  const [loading, setLoading] = useState(false);

  // OTP flow state — one of: 'idle' | 'email' | 'code' | 'verified'
  const [verifyStep, setVerifyStep] = useState("idle");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyErr, setVerifyErr] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifiedAs, setVerifiedAs] = useState(null); // {email, expires_at}

  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const convoRef = useRef(null);

  const chips = chipsForPath(location.pathname);
  const hasUserTurn = messages.some((m) => m.role === "user");

  // First-visit pulse (once per browser).
  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY_PULSED)) {
        setPulse(true);
        const t = setTimeout(() => {
          setPulse(false);
          try { localStorage.setItem(STORAGE_KEY_PULSED, "1"); } catch { /* noop */ }
        }, 4200);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    convoRef.current = getOrCreateConversationId();
  }, []);

  // Restore verified-session banner on mount (page reload).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/public/support/otp/session");
        if (!cancelled && r.data?.verified) {
          setVerifiedAs({ email: r.data.email, expires_at: r.data.expires_at });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-scroll on new messages / verify step change.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading, verifyStep]);

  // ESC closes.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (open && verifyStep === "idle" && inputRef.current) inputRef.current.focus();
  }, [open, verifyStep]);

  const ask = async (overrideText) => {
    const source = typeof overrideText === "string" ? overrideText : q;
    const trimmed = (source || "").trim();
    if (!trimmed || loading) return;
    if (typeof overrideText !== "string") setQ("");
    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setLoading(true);
    try {
      const r = await api.post("/public/support/ask", {
        question: trimmed,
        conversation_id: convoRef.current,
      });
      const d = r.data || {};
      if (d.matched && d.answer) {
        setMessages((prev) => [
          ...prev,
          { role: "bot", text: d.answer, questionId: d.question_id, canonicalIntent: d.canonical_intent },
        ]);
      } else if (d.require_signin && !verifiedAs) {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text:
              "I don't have a confirmed answer for that yet. Sign in, create an account, or verify your email so a Cyannect specialist can pick it up.",
            requireSignin: true,
            offerVerify: true,
          },
        ]);
      } else {
        // No match, but caller is verified (or logged in) — escalate.
        await escalateVerified(trimmed);
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 429
          ? "You're sending questions a little quickly. Please wait a moment and try again."
          : "Something went wrong. Please try again in a moment.";
      setMessages((prev) => [...prev, { role: "bot", text: msg, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const escalateVerified = async (text) => {
    try {
      const r = await api.post("/public/support/escalate", {
        question: text,
        conversation_id: convoRef.current,
      });
      const d = r.data || {};
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text:
            d.message ||
            `Thanks — a Cyannect specialist will reply to ${d.verified_email || verifiedAs?.email || "your inbox"} shortly.`,
          escalated: true,
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text:
            "I couldn't route that to Cyannect Support just now. Please try again in a moment or email hello@cyannect.com.",
          error: true,
        },
      ]);
    }
  };

  const startVerifyFlow = () => {
    setVerifyErr("");
    setVerifyCode("");
    if (verifiedAs) {
      setVerifyStep("idle");
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: `You're verified as ${verifiedAs.email}. Type your question below and I'll route it to a Cyannect specialist.`,
        },
      ]);
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    setVerifyStep("email");
    setMessages((prev) => [
      ...prev,
      {
        role: "bot",
        text:
          "Sure — I'll route this to a Cyannect specialist. Please sign in, create an account, or verify your email with a one-time code so we can reply to you.",
        offerVerify: true,
      },
    ]);
  };

  const requestOtp = async () => {
    setVerifyErr("");
    if (!verifyEmail.trim()) return;
    setVerifyBusy(true);
    try {
      await api.post("/public/support/otp/request", { email: verifyEmail.trim() });
      setVerifyStep("code");
    } catch (e) {
      const status = e?.response?.status;
      setVerifyErr(
        status === 429
          ? "Too many code requests. Please wait a few minutes and try again."
          : "We couldn't send the code. Double-check the email and retry.",
      );
    } finally {
      setVerifyBusy(false);
    }
  };

  const verifyOtp = async () => {
    setVerifyErr("");
    if (!verifyCode.trim()) return;
    setVerifyBusy(true);
    try {
      const r = await api.post("/public/support/otp/verify", {
        email: verifyEmail.trim(),
        code: verifyCode.trim(),
      });
      const d = r.data || {};
      setVerifiedAs({ email: d.email, expires_at: d.expires_at });
      setVerifyStep("idle");
      setVerifyCode("");
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: `Great — verified as ${d.email}. Ask your question below and I'll route it to a Cyannect specialist.`,
          verifiedBanner: true,
        },
      ]);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      const status = e?.response?.status;
      const remaining = e?.response?.data?.detail?.attempts_remaining;
      if (status === 410) {
        setVerifyErr("That code has expired. Please request a new one.");
        setVerifyStep("email");
      } else if (status === 429) {
        setVerifyErr("Too many attempts. Please request a new code in a few minutes.");
      } else if (typeof remaining === "number") {
        setVerifyErr(`Incorrect code. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`);
      } else {
        setVerifyErr("Incorrect code. Please try again.");
      }
    } finally {
      setVerifyBusy(false);
    }
  };

  const cancelVerify = () => {
    setVerifyStep("idle");
    setVerifyErr("");
    setVerifyCode("");
  };

  const onChipClick = (chip) => {
    if (chip.question === CHIP_ESCALATE) {
      startVerifyFlow();
      return;
    }
    ask(chip.question);
  };

  const vote = async (msgIdx, kind) => {
    const m = messages[msgIdx];
    if (!m || !m.questionId || m.voted) return;
    setMessages((prev) =>
      prev.map((x, i) => (i === msgIdx ? { ...x, voted: kind } : x)),
    );
    try {
      await api.post(`/public/support/feedback/${m.questionId}`, { vote: kind });
    } catch {
      /* silent — vote already reflected optimistically */
    }
  };

  const resetChat = () => {
    convoRef.current = resetConversationId();
    setMessages([WELCOME]);
    setQ("");
    setVerifyStep("idle");
    setVerifyErr("");
    setVerifyCode("");
    // NB: verifiedAs sticks — it's a server-side session cookie. Reset
    // only wipes local chat state.
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="support-chat-launcher"
          aria-label="Ask Cyannect"
          className="fixed z-[1000] bottom-4 right-4 md:bottom-6 md:right-6 flex items-center gap-2 rounded-full text-white px-4 py-3 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all support-chat-launcher-btn"
          style={{
            background: "var(--cy-burgundy, #4A0D18)",
            boxShadow: "0 10px 30px rgba(74, 13, 24, 0.35)",
          }}
        >
          <span className="relative flex h-6 w-6 items-center justify-center">
            <MessageCircle className="h-5 w-5" />
            {pulse && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-40" />
            )}
          </span>
          <span className="text-sm font-semibold hidden sm:inline">Ask Cyannect</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="support-chat-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Ask Cyannect chat"
          className="fixed z-[1000] bottom-4 right-4 md:bottom-6 md:right-6 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[420px] h-[560px] max-h-[calc(100vh-2rem)] flex flex-col rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
          style={{ boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 text-white"
               style={{ background: "var(--cy-burgundy, #4A0D18)" }}>
            <div className="flex items-center gap-2 min-w-0">
              <MessageCircle className="w-5 h-5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">Ask Cyannect</div>
                <div className="text-[11px] opacity-80 leading-tight">Deterministic answers · beta</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={resetChat}
                data-testid="support-chat-reset"
                aria-label="Start a new chat"
                className="p-1.5 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
                title="New chat"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="support-chat-close"
                aria-label="Close chat"
                className="p-1.5 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Message list */}
          <div
            ref={scrollerRef}
            data-testid="support-chat-messages"
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
          >
            {/* Verified banner */}
            {verifiedAs && (
              <div
                data-testid="support-chat-verified-banner"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs border"
                style={{
                  background: "rgba(16, 185, 129, 0.08)",
                  borderColor: "rgba(16, 185, 129, 0.35)",
                  color: "#047857",
                }}
              >
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>Verified as <span className="font-semibold">{verifiedAs.email}</span></span>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} msg={m} index={i} onVote={vote} onVerify={startVerifyFlow} />
            ))}

            {/* Inline OTP verification UI */}
            {verifyStep !== "idle" && (
              <div
                data-testid="support-chat-verify-panel"
                className="rounded-xl border border-slate-200 bg-white p-3 space-y-2"
              >
                <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "var(--cy-burgundy, #4A0D18)" }}>
                  <Mail className="w-4 h-4" />
                  {verifyStep === "email" ? "Verify your email" : "Enter your code"}
                </div>
                {verifyStep === "email" && (
                  <>
                    <p className="text-xs text-slate-600">
                      We'll email a 6-digit code so a Cyannect specialist can reply to you.
                    </p>
                    <input
                      type="email"
                      value={verifyEmail}
                      onChange={(e) => setVerifyEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                      placeholder="you@example.com"
                      data-testid="support-chat-otp-email"
                      className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2"
                      disabled={verifyBusy}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={requestOtp}
                        disabled={verifyBusy || !verifyEmail.trim()}
                        data-testid="support-chat-otp-send"
                        className="rounded-md text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-40"
                        style={{ background: "var(--cy-burgundy, #4A0D18)" }}
                      >
                        {verifyBusy ? "Sending…" : "Send code"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelVerify}
                        data-testid="support-chat-otp-cancel"
                        className="text-xs text-slate-500 hover:text-slate-700"
                      >
                        Cancel
                      </button>
                      <span className="ml-auto text-[11px] text-slate-400">
                        or{" "}
                        <a href="/login" className="font-semibold hover:underline" style={{ color: "var(--cy-burgundy, #4A0D18)" }} data-testid="support-chat-otp-signin">
                          Sign in
                        </a>{" "}
                        /{" "}
                        <a href="/register" className="font-semibold hover:underline" style={{ color: "var(--cy-burgundy, #4A0D18)" }} data-testid="support-chat-otp-register">
                          Register
                        </a>
                      </span>
                    </div>
                  </>
                )}
                {verifyStep === "code" && (
                  <>
                    <p className="text-xs text-slate-600">
                      We sent a 6-digit code to <span className="font-semibold">{verifyEmail}</span>. It expires in 10 minutes.
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      onKeyDown={(e) => e.key === "Enter" && verifyOtp()}
                      placeholder="123456"
                      data-testid="support-chat-otp-code"
                      className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 tracking-widest text-center"
                      disabled={verifyBusy}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={verifyOtp}
                        disabled={verifyBusy || verifyCode.trim().length < 4}
                        data-testid="support-chat-otp-verify"
                        className="rounded-md text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-40"
                        style={{ background: "var(--cy-burgundy, #4A0D18)" }}
                      >
                        {verifyBusy ? "Verifying…" : "Verify"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setVerifyStep("email"); setVerifyCode(""); setVerifyErr(""); }}
                        data-testid="support-chat-otp-resend"
                        className="text-xs text-slate-500 hover:text-slate-700"
                      >
                        Change email
                      </button>
                    </div>
                  </>
                )}
                {verifyErr && (
                  <div data-testid="support-chat-otp-error" className="text-xs text-red-600">
                    {verifyErr}
                  </div>
                )}
              </div>
            )}

            {/* Starter chips — only before the first user turn AND not
                during an active OTP flow. Chips include "Something else"
                which triggers the OTP path. */}
            {!hasUserTurn && !loading && verifyStep === "idle" && (
              <div
                data-testid="support-chat-chips"
                className="flex flex-wrap gap-2 pt-1 pl-1"
              >
                {chips.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => onChipClick(c)}
                    data-testid={`support-chat-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-xs px-3 py-1.5 rounded-full border bg-white hover:bg-slate-100 transition-colors"
                    style={{
                      borderColor: "var(--cy-burgundy, #4A0D18)",
                      color: "var(--cy-burgundy, #4A0D18)",
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            {loading && (
              <div data-testid="support-chat-typing" className="text-xs text-slate-500 pl-1">
                Cyannect is thinking…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-2.5 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
                rows={1}
                maxLength={1000}
                placeholder={verifiedAs ? "Type your question — a specialist will reply by email…" : "Type your question…"}
                data-testid="support-chat-input"
                className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:border-slate-500 outline-none"
                style={{ maxHeight: 120 }}
                disabled={verifyStep !== "idle"}
              />
              <button
                type="button"
                onClick={ask}
                disabled={loading || !q.trim()}
                data-testid="support-chat-send"
                aria-label="Send"
                className="shrink-0 rounded-lg text-white p-2 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2"
                style={{ background: "var(--cy-burgundy, #4A0D18)" }}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-1.5 text-[10px] text-slate-400 px-1">
              Answers come from Cyannect's approved knowledge base.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ msg, index, onVote, onVerify }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid={`support-chat-msg-user-${index}`}>
        <div className="max-w-[80%] rounded-2xl rounded-br-sm text-white px-3 py-2 text-sm whitespace-pre-wrap"
             style={{ background: "var(--cy-burgundy, #4A0D18)" }}>
          {msg.text}
        </div>
      </div>
    );
  }
  const testid = `support-chat-msg-bot-${index}`;
  return (
    <div className="flex justify-start" data-testid={testid}>
      <div className="max-w-[85%]">
        <div
          className={`rounded-2xl rounded-bl-sm px-3 py-2 text-sm whitespace-pre-wrap border ${
            msg.error
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-white border-slate-200 text-slate-800"
          }`}
        >
          {msg.text}
        </div>

        {/* Sign-in / register / verify CTA on no-match for anonymous callers */}
        {(msg.requireSignin || msg.offerVerify) && (
          <div className="mt-1.5 flex flex-wrap gap-2 text-xs">
            <a
              href="/login"
              data-testid="support-chat-signin-link"
              className="rounded-md text-white px-2.5 py-1 font-semibold"
              style={{ background: "var(--cy-burgundy, #4A0D18)" }}
            >
              Sign in
            </a>
            <a
              href="/register"
              data-testid="support-chat-register-link"
              className="rounded-md border px-2.5 py-1 font-semibold"
              style={{ borderColor: "var(--cy-burgundy, #4A0D18)", color: "var(--cy-burgundy, #4A0D18)" }}
            >
              Create account
            </a>
            {msg.offerVerify && (
              <button
                type="button"
                onClick={onVerify}
                data-testid="support-chat-inline-verify"
                className="rounded-md border bg-white px-2.5 py-1 font-semibold hover:bg-slate-50"
                style={{ borderColor: "var(--cy-burgundy, #4A0D18)", color: "var(--cy-burgundy, #4A0D18)" }}
              >
                Verify email
              </button>
            )}
          </div>
        )}

        {/* Email escalation on signed-in miss */}
        {msg.supportEmail && (
          <div className="mt-1.5 text-xs">
            <a
              href={`mailto:${msg.supportEmail}`}
              data-testid="support-chat-email-link"
              className="font-semibold hover:underline"
              style={{ color: "var(--cy-burgundy, #4A0D18)" }}
            >
              Email {msg.supportEmail}
            </a>
          </div>
        )}

        {/* Vote controls on matched answers */}
        {msg.questionId && !msg.error && (
          <div className="mt-1 flex items-center gap-2 pl-1">
            <button
              type="button"
              onClick={() => onVote(index, "helpful")}
              disabled={!!msg.voted}
              data-testid={`support-chat-helpful-${index}`}
              className="text-[11px] text-slate-500 hover:text-emerald-600 disabled:opacity-40"
            >
              {msg.voted === "helpful" ? "👍 Thanks" : "👍 Helpful"}
            </button>
            <button
              type="button"
              onClick={() => onVote(index, "not_helpful")}
              disabled={!!msg.voted}
              data-testid={`support-chat-unhelpful-${index}`}
              className="text-[11px] text-slate-500 hover:text-red-600 disabled:opacity-40"
            >
              {msg.voted === "not_helpful" ? "👎 Logged" : "👎 Not helpful"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
